import {
  DashboardMetricTile,
  DashboardPanel,
} from "@/dashboard/components/dashboard-ds";
import { Badge, Button } from "@woba/ds-web-next";
import { useTranslation } from "react-i18next";
import { useCompanyHighlights } from "@/company/hooks/use-company-highlights";
import { useAuth } from "@/core/components/auth/store/auth";
import { useChatbot } from "@/core/components/floating-chatbot/context/chatbot-context";
import { useActionsTrack } from "@/_old/features/analytics";
import { Skeleton } from "@/core/components/ui/skeleton";
import { formatInteger, useIntlLocale } from "@/core/utils/locale";

export function CollaboratorsCard() {
  const [t] = useTranslation(["company"]);
  const [tCore] = useTranslation(["core"]);
  const [tDashboard] = useTranslation(["dashboard"]);
  const {
    userInfo: { user },
  } = useAuth();
  const { openWithMessage } = useChatbot();
  const locale = useIntlLocale();
  const { onActionClick } = useActionsTrack();

  const { data: companyHighlights, isLoading } = useCompanyHighlights({
    companyId: user.leagues_id[0],
  });

  const registeredMembers = companyHighlights?.registeredMembers || 0;
  const pendingMembers = companyHighlights?.pendingMembers || 0;

  const handleManageMembers = () => {
    onActionClick(
      "DASHBOARD_COLLABORATORS_CARD_MANAGE_MEMBERS_CLICK",
      "DASHBOARD",
      {
        registeredMembers,
        pendingMembers,
      },
    );
    openWithMessage(tCore("chatbot-manage-members-action"));
  };

  // Figma 6087:23905: título + descrição + selo "Woba AI", dois tiles de
  // métrica e o Button secundário que abre a Bruna.
  const panelProps = {
    "data-testid": "collaborators-card",
    title: t("company-heading-title", "Colaboradores"),
    description: tDashboard("dashboard-collaborators-card-description"),
    badge: (
      <Badge
        variant="intelligence"
        label={tDashboard("exceeded-credits-badge-woba-ia") as string}
      />
    ),
    className: "h-full",
  };

  if (isLoading) {
    return (
      <DashboardPanel
        {...panelProps}
        footer={<Skeleton className="w-full h-9 rounded-full" />}
      >
        <div className="grid grid-cols-2 gap-2">
          <Skeleton className="h-[92px] rounded-3xl" />
          <Skeleton className="h-[92px] rounded-3xl" />
        </div>
      </DashboardPanel>
    );
  }

  return (
    <DashboardPanel
      {...panelProps}
      footer={
        <Button
          appearance="secondary"
          size="small"
          style={{ width: "100%" }}
          label={
            tDashboard("dashboard-collaborators-resolve-pending") as string
          }
          onPress={handleManageMembers}
        />
      }
    >
      <div className="grid grid-cols-2 gap-2">
        <DashboardMetricTile
          variant="heading"
          value={formatInteger(registeredMembers, locale)}
          caption={
            <span className="lowercase">{t("company-tabs-members-label")}</span>
          }
        />
        <DashboardMetricTile
          variant="heading"
          value={formatInteger(pendingMembers, locale)}
          caption={
            <span className="lowercase">
              {t("company-highlights-pending-members-title")}
            </span>
          }
        />
      </div>
    </DashboardPanel>
  );
}
